import { useEffect, useRef } from "react";
import { useSnackbar } from "notistack";
import { getServerUri } from "../modules/uriUtility";

const TEST_PROD_ENVS = ["test", "production"];
const POLL_INTERVAL = 60000;

export default function StickyVersionUpdateWarning(props) {
  const accessToken = localStorage.getItem("session.accessToken");
  const { closeSnackbar } = useSnackbar();
  const testProdRef = useRef({ key: null, count: 0 });
  const developmentRef = useRef({ key: null, count: 0 });

  const hasPermission = props.userPermissions && props.userPermissions.bulkUpdateDatabase === true;
  const showTestProd = hasPermission && props.appEnv !== "production";
  const showDevelopment = hasPermission && props.appEnv === "development";
  const isActive = showTestProd || showDevelopment;

  useEffect(() => {
    const dismiss = (ref) => {
      if (ref.current.key !== null) {
        closeSnackbar(ref.current.key);
      }
      ref.current = { key: null, count: 0 };
    };

    if (!isActive || props.suppress) {
      dismiss(testProdRef);
      dismiss(developmentRef);
      return undefined;
    }

    let cancelled = false;

    const sync = (ref, count, message) => {
      if (cancelled) return;
      if (count === 0) {
        dismiss(ref);
        return;
      }
      if (ref.current.key !== null && ref.current.count === count) {
        return;
      }
      if (ref.current.key !== null) {
        closeSnackbar(ref.current.key);
      }
      const key = props.createSnackbar(message, "warning", true, props.onOpen, "Open Version Update");
      ref.current = { key, count };
    };

    const load = async () => {
      try {
        const formBody = new URLSearchParams();
        formBody.append("accessToken", accessToken);
        const response = await fetch(getServerUri("versionTransfer/updateDatabaseEntryList"), {
          method: "POST",
          headers: { "content-type": "application/x-www-form-urlencoded" },
          body: formBody.toString()
        });
        if (!response.ok) {
          return;
        }
        const data = await response.json();
        if (cancelled) return;
        const entries = (data && data.entries) || [];
        const developmentBaseIDs = new Set((data && data.developmentBaseIDs) || []);

        if (showTestProd) {
          const testProdCount = entries.filter((entry) =>
            TEST_PROD_ENVS.some((env) => !(entry.envs && entry.envs[env]))
          ).length;
          sync(
            testProdRef,
            testProdCount,
            "⚠ " + testProdCount + " version update(s) still need to be applied to the test and production databases."
          );
        }

        if (showDevelopment) {
          const developmentCount = entries.filter(
            (entry) =>
              !(entry.envs && entry.envs.development) &&
              entry.baseID &&
              developmentBaseIDs.has(String(entry.baseID))
          ).length;
          sync(
            developmentRef,
            developmentCount,
            "⚠ " + developmentCount + " version update(s) still need to be applied to the development database."
          );
        }
      } catch (err) {
        console.error(err);
      }
    };

    load();
    const interval = setInterval(load, POLL_INTERVAL);
    return () => {
      cancelled = true;
      clearInterval(interval);
      dismiss(testProdRef);
      dismiss(developmentRef);
    };
  }, [isActive, showTestProd, showDevelopment, props.appEnv, props.suppress]);

  return null;
}
